import { normalize } from 'normalizr';
import {
  question,
  article,
  listItem,
  video,
} from '@/dex-shared/helpers/normalizr_schema';
import { query, mutation } from '@/dex-shared/helpers/graphql';
import ListItemModel from '@/dex-shared/models/ListItemModel';
import QuestionModel from '@/dex-shared/models/QuestionModel';
import ArticleModel from '@/dex-shared/models/ArticleModel';
import VideoModel from '@/dex-shared/models/VideoModel';
import { authenticate, getVideoInfo } from '@/helpers/kaltura';

export default {
  async DELETE_LIST_ITEM({ commit, getters }: any, listItemId: number) {
    await mutation(
      `mutation DeleteListItem($listItemId: Int!) {
        deleteListItemById(input: {
          id: $listItemId
        }) {
          listItem {
            id
          }
        }
      }`,
      {
        listItemId,
      },
    );
    const listItems = getters
      .listItemById(listItemId)
      .list()
      .listItems();

    commit('DELETE_ENTITY', {
      entity: 'list_items',
      id: listItemId,
    });

    const listItemsToModify = listItems.filter(
      (listItem: ListItemModel) => listItem.id != listItemId,
    );

    listItemsToModify.forEach((listItem: ListItemModel, position: number) => {
      commit('UPDATE_ENTITY_PROP', {
        id: listItem.id,
        entity: 'list_items',
        propToUpdate: 'position',
        value: position,
      });
    });
  },
  async PULL_QUESTION_FOR_LIST_ITEM({ commit }: any, questionId: number) {
    const questionResponse = await query(
      `query PullQuestionForListItem ($questionId: Int!) {
        questionById(id: $questionId) {
          id
          title
          contentsByQuestionId {
            nodes {
              id
              position
              questionId
              textByTextId {
                id
                body
                alignment
                source
              }
              imageByImageId {
                id
                url
                source
                resizeFactor
                description
              }
              videoByVideoId {
                id
                providerId
                providerType
              }
            }
          }
        }
      }`,
      {
        questionId,
      },
    );
    if (questionResponse.data.questionById) {
      const normalizedResponse = normalize(
        questionResponse.data.questionById,
        question,
      );
      commit('SET_NORMALIZED_DATA', normalizedResponse.entities);
    } else {
      return 404;
    }
  },
  async PULL_ARTICLE_FOR_LIST_ITEM({ commit }: any, articleId: number) {
    const articleResponse = await query(
      `query PullArticleForListItem ($articleId: Int!) {
        articleById(id: $articleId) {
          id
          title
          contentsByArticleId {
            nodes {
              id
              position
              questionId
              textByTextId {
                id
                body
                alignment
                source
              }
              imageByImageId {
                id
                url
                source
                resizeFactor
                description
              }
              videoByVideoId {
                id
                providerId
                providerType
              }
            }
          } 
        }
      }`,
      {
        articleId,
      },
    );
    if (articleResponse.data.articleById) {
      const normalizedResponse = normalize(
        articleResponse.data.articleById,
        article,
      );
      commit('SET_NORMALIZED_DATA', normalizedResponse.entities);
    } else {
      return 404;
    }
  },
  async CREATE_LIST_ITEM_FROM_ID(
    { commit, getters, rootState, dispatch }: any,
    {
      questionId,
      articleId,
      listId,
      videoId,
      position,
    }: {
      questionId: number;
      articleId: number;
      listId: number;
      position: number;
      videoId: number;
    },
  ) {
    const userId = rootState.auth.user.id;
    if (questionId) {
      await dispatch('PULL_QUESTION_FOR_LIST_ITEM', questionId);
    }
    if (articleId) {
      await dispatch('PULL_ARTICLE_FOR_LIST_ITEM', articleId);
    }
    if (position == 0) {
      getters
        .listById(listId)
        .listItems()
        .forEach((listItem: ListItemModel) => {
          commit('UPDATE_ENTITY_PROP', {
            id: listItem.id,
            entity: 'list_items',
            propToUpdate: 'position',
            value: listItem.position + 1,
          });
        });
      await dispatch(
        'UPDATE_LIST_POSITIONS',
        getters.listById(listId).listItems(),
      );
    }
    const listItemResponse = await mutation(
      `mutation CreateListItem($userId: Int!, $listId: Int!, $questionId: Int, $articleId: Int, $videoId: Int, $position: Int!) {
        createListItem(input: {
          listItem: {
            userId: $userId
            listId: $listId
            questionId: $questionId
            videoId: $videoId
            articleId: $articleId
            position: $position
          }
        }) {
          listItem {
            id
            listId
            userId
            questionId
            videoId
            articleId
            position
          }
        }
      }`,
      {
        userId,
        listId,
        questionId,
        articleId,
        videoId,
        position,
      },
    );
    commit('UPDATE_ENTITY', {
      entity: 'list_items',
      data: listItemResponse.data.createListItem.listItem,
    });
  },
  async PULL_VIDEO_FOR_LIST_ITEM({ commit }: any, videoId: number) {
    const videoResponse = await query(
      `query PullVideoForListItem ($kalturaVideoId: String!) {
        allVideos(filter: { providerId: { equalTo: $kalturaVideoId } } ) {
          nodes {
            id
            providerType
            providerId
            name
            description
          }
        }
      }`,
      {
        kalturaVideoId: videoId,
      },
    );
    let videoPayload = videoResponse.data.allVideos.nodes[0];
    if (videoPayload) {
      const normalizedResponse = normalize(videoPayload, video);
      commit('SET_NORMALIZED_DATA', normalizedResponse.entities);
      return videoPayload.id;
    }
  },
  async PULL_KALTURA_VIDEO_DURATION(_, videoId: string) {
    try {
      await authenticate();

      const videoInformation = await getVideoInfo(videoId);

      return videoInformation.duration || 0;
    } catch (err) {
      return null;
    }
  },
  async CREATE_VIDEO_FOR_LIST_ITEM(
    { commit, rootState }: any,
    videoInfo: VideoModel,
  ) {
    const videoInformation = {
      videoId: videoInfo.providerId,
      providerType: videoInfo.providerType,
      duration: videoInfo.duration,
      description: videoInfo.description,
      name: videoInfo.name,
      userId: rootState.auth.user.id,
    };
    const newlyCreatedVideoResponse = await mutation(
      `
      mutation CreateVideoForListItem($videoId: String!, $providerType: Videoproviderenum!, $userId: Int!, $duration: Int, $description: String, $name: String) {
        createVideo(input: { video: { providerType: $providerType, providerId: $videoId, userId: $userId, duration: $duration, description: $description, name: $name } }) {
          video {
            id
            providerType
            providerId
            name
            description
            duration
            userId
          }
        }
      }
    `,
      videoInformation,
    );
    let createdVideoPayload = newlyCreatedVideoResponse.data.createVideo.video;
    const normalizedResponse = normalize(createdVideoPayload, video);
    commit('SET_NORMALIZED_DATA', normalizedResponse.entities);
    return createdVideoPayload.id;
  },
  async UPDATE_LIST_POSITIONS(_: any, listItems: Array<ListItemModel>) {
    const editListItems = listItems.map((listItem: ListItemModel) => ({
      id: listItem.id,
      position: listItem.position,
    }));
    await mutation(
      `mutation UpdatePosition($listItems: [ListItemEdit]) {
        updateListItems(
          items: $listItems
        )
      }`,
      {
        listItems: editListItems,
      },
    );
  },
  async CREATE_ITEMS_FROM_TAG(
    { commit, getters, rootState }: any,
    { listId, tag }: { listId: number; tag: string },
  ) {
    const list = getters.listById(listId);
    const userId = rootState.auth.user.id;
    const response = await query(
      `query PullItemsByGroup($tag: String!) {
        allArticles(condition: {
          group: $tag
        }) {
          nodes {
            id
            title
            contentsByArticleId {
              nodes {
                id
                position
                articleId
                textByTextId {
                  id
                  body
                  alignment
                  source
                }
                imageByImageId {
                  id
                  url
                  source
                  resizeFactor
                  description
                }
                videoByVideoId {
                  id
                  providerId
                  providerType
                }
              }
            }       
          }
        }
        questionsByTag(tag: $tag) {
          nodes {
            id
            title
            contentsByQuestionId {
              nodes {
                id
                position
                questionId
                textByTextId {
                  id
                  body
                  alignment
                  source
                }
                imageByImageId {
                  id
                  url
                  source
                  resizeFactor
                  description
                }
                videoByVideoId {
                  id
                  providerId
                  providerType
                }
              }
            }  
          }
        }
      }`,
      {
        tag,
      },
    );

    let listQuestionsToAdd = [];
    let questionEntities = {};
    if (response.data.questionsByTag.nodes.length > 0) {
      const normalizedQuestions = normalize(
        response.data.questionsByTag.nodes,
        [question],
      );
      const questionsToAdd = Object.keys(
        normalizedQuestions.entities.questions,
      ).filter(
        (questionId: string) =>
          !list
            .questions()
            .map((question: QuestionModel) => question.id)
            .includes(+questionId),
      );
      listQuestionsToAdd = questionsToAdd.map((questionId: string) => ({
        questionId: +questionId,
        articleId: null,
      }));
      questionEntities = normalizedQuestions.entities;
    }

    let listArticlesToAdd = [];
    let articleEntities = {};
    if (response.data.allArticles.nodes.length > 0) {
      const normalizedArticles = normalize(response.data.allArticles.nodes, [
        article,
      ]);
      const articlesToAdd: any = Object.keys(
        normalizedArticles.entities.articles,
      ).filter(
        (articleId: string) =>
          !list
            .articles()
            .map((article: ArticleModel) => article.id)
            .includes(+articleId),
      );
      listArticlesToAdd = articlesToAdd.map((articleId: string) => ({
        articleId: +articleId,
        questionId: null,
      }));
      articleEntities = normalizedArticles.entities;
    }

    const listItemsToCreate = listQuestionsToAdd.concat(listArticlesToAdd);
    if (listItemsToCreate.length > 0) {
      const positionOffset =
        list.listItems().length > 0
          ? list.listItems().slice(-1)[0].position + 1
          : 0;
      const listItemsResponse = await mutation(
        `mutation CreateMultipleListItems($listId: Int!, $userId: Int!) {
          ${listItemsToCreate
            .map((item: any, index: number) => {
              return `listItem${index}: createListItem(
                input: {
                  listItem: {
                    listId: $listId,
                    articleId: ${item.articleId},
                    questionId: ${item.questionId},
                    position: ${index + positionOffset},
                    userId: $userId
                  }
                }
              ) {
                listItem {
                  id
                  questionId
                  articleId
                  position
                  listId
                }
              }`;
            })
            .join('')}
        }`,
        {
          listId,
          userId,
        },
      );
      const listItems = Object.values(listItemsResponse.data).map(
        (payload: any) => payload.listItem,
      );
      const normalizedListItems = normalize(listItems, [listItem]);
      commit('SET_NORMALIZED_DATA', normalizedListItems.entities);
      commit('SET_NORMALIZED_DATA', articleEntities);
      commit('SET_NORMALIZED_DATA', questionEntities);
    }
  },
};
